// $localize must run inside the es-module-shims graph (not the native polyfills
// bundle): polyfills execute before the import map exists, so shimmed remotes
// cannot see a polyfill-only init. Top-of-main is the supported pattern for
// native-federation shimMode. Do not move this to polyfills or index.html alone.
import '@angular/localize/init';
import { initFederation } from '@angular-architects/native-federation';
import { recordRemoteBootState } from './app/shared/remote-boot-state';
import { rebaseRemotesOntoPageHost } from './app/shared/remote-origin.utils';

// Ensure ngDevMode is defined before any Angular module is evaluated.
// Native federation can load shared Angular chunks before the platform
// bootstraps, causing "ngDevMode is not defined" in component class fields.
(globalThis as Record<string, unknown>)['ngDevMode'] ??= false;

// Remove ?mode=new query param if present (legacy handoff param, not needed in shell).
const _initUrl = new URL(window.location.href);
if (_initUrl.searchParams.has('mode')) {
  _initUrl.searchParams.delete('mode');
  window.history.replaceState(null, '', _initUrl.toString());
}

// Detect locale from URL path. Both locales use explicit prefixes:
// /en/* for English, /ar/* for Arabic.
const isArabic = window.location.pathname.startsWith('/ar/') || window.location.pathname === '/ar';
const locale = isArabic ? 'ar' : 'en';

// Resolve federation manifest relative to <base href> so each locale's shell
// build loads its own federation.manifest.json (e.g. /en/federation.manifest.json).
const manifestUrl = new URL('federation.manifest.json', document.baseURI).href;

/**
 * Reads the manifest that `scripts/inline-federation-manifest.js` embedded at
 * build time. Skipping the fetch removes a round trip nothing can overlap:
 * `initFederation` gates the whole boot, and this URL is computed at runtime so
 * the preload scanner never sees it. Returns null for `nx serve` and any build
 * that did not run the script, where the fetch below stays the live path.
 */
function readInlineManifest(): Record<string, string> | null {
  const el = document.getElementById('federation-manifest');
  if (!el?.textContent) return null;
  try {
    const parsed = JSON.parse(el.textContent) as Record<string, string>;
    return Object.keys(parsed).length > 0 ? parsed : null;
  } catch {
    console.warn('[federation] inline manifest is not valid JSON; falling back to fetch');
    return null;
  }
}

/**
 * The dev server answers `index.html` before native-federation has finished
 * writing the host's own `remoteEntry.json`, so an early boot parses the SPA
 * fallback HTML as JSON and throws. Retry instead of leaving the preloader up
 * until the developer refreshes by hand.
 */
async function initFederationWhenReady(
  manifest: Record<string, string>,
  attempts = 8,
  delayMs = 500,
): Promise<void> {
  for (let attempt = 1; ; attempt++) {
    try {
      await initFederation(manifest);
      return;
    } catch (err) {
      if (attempt >= attempts) throw err;
      await new Promise((r) => setTimeout(r, delayMs));
    }
  }
}

async function fetchManifest(): Promise<Record<string, string>> {
  const res = await fetch(manifestUrl);
  if (!res.ok) {
    throw new Error(`Failed to load federation manifest (${res.status})`);
  }
  return (await res.json()) as Record<string, string>;
}

void (async () => {
  try {
    const manifest = rebaseRemotesOntoPageHost(
      readInlineManifest() ?? (await fetchManifest()),
      window.location,
    );

    // Rewrite remote URLs to point to the locale-specific AOT build.
    // The manifest always references the /en/ build (source locale).
    // For other locales, replace /en/ with the target locale prefix.
    // e.g. .../en/dashboard/remoteEntry.json → .../ar/dashboard/remoteEntry.json
    if (locale !== 'en') {
      for (const [key, url] of Object.entries(manifest)) {
        manifest[key] = url.replace('/en/', `/${locale}/`);
      }
    }

    // The entry URLs the failure page and the lazy remote loader both read.
    recordRemoteBootState({ manifest });
    // Host-only: every remote registers itself on first use (see
    // `remote-route-loader.ts`). Registering all five up front cost a blocking
    // round trip per remote before Angular could start, on every cold entry —
    // including sign-in, which reaches no remote at all. Safe because no remote
    // declares a shared version the host disagrees with, and the four
    // specifiers only remotes share are ones the host never resolves itself.
    await initFederationWhenReady({});
    await import('./bootstrap');
  } catch (err) {
    console.error('Federation init error:', err);
  }
})();
